import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import { useUserHasAccess } from '@backoffice-frontend/restricted'
import { BoumRight } from '@moia-dev/moia-token-claims'
import {
  Combobox,
  FormField,
  FormFieldValidation,
  LoadingIndicator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@moia-dev/pace-web'
import uniqBy from 'lodash/uniqBy'
import { useId, useRef, useState } from 'react'
import {
  type Control,
  type FieldPathByValue,
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toAssigneeOptions } from './utils'
import { useWorkOrderAssigneesQuery } from './WorkOrderAssignees.hook'

export type WorkOrderAssigneeOption = {
  id: string
  name: string
}

export type WorkOrderAssigneeFieldProps<
  FormValues extends FieldValues,
  Name extends FieldPathByValue<
    FormValues,
    WorkOrderAssigneeOption | null
  > = FieldPathByValue<FormValues, WorkOrderAssigneeOption | null>,
> = {
  control: Control<FormValues>
  name: Name
  rules?: UseControllerProps<FormValues, Name>['rules']
  disabled?: boolean
}

const CLEAR_QUERY_DELAY_MS = 400

// Only users who can work on work orders can be assigned to one; either right
// is enough.
const ASSIGNEE_RIGHTS = {
  crudRights: [BoumRight.WORK_ORDERS_CRUD.str()],
  cruRights: [BoumRight.WORK_ORDERS_CRU.str()],
}

export const WorkOrderAssigneeField = <
  FormValues extends FieldValues,
  Name extends FieldPathByValue<
    FormValues,
    WorkOrderAssigneeOption | null
  > = FieldPathByValue<FormValues, WorkOrderAssigneeOption | null>,
>({
  control,
  name,
  rules,
  disabled,
}: WorkOrderAssigneeFieldProps<FormValues, Name>) => {
  const { t } = useTranslation()
  const id = useId()
  const [query, setQuery] = useState<string | null>(null)

  const canLookUpAssignees = useUserHasAccess({
    requiredRights: [
      // To get the list of users.
      BoumRight.BACKOFFICE_USER_USER_R,
      // Guard related to the query itself: `backofficeUsersByRights`.
      BoumRight.BACKOFFICE_USER_RIGHTS_R,
    ],
  })

  const { data, loading } = useErrorHandlingQuery(useWorkOrderAssigneesQuery, {
    variables: ASSIGNEE_RIGHTS,
    skip: !canLookUpAssignees,
  })
  // Users holding both rights are in both lists
  const assignees = uniqBy(
    [...(data?.crudAssignees ?? []), ...(data?.cruAssignees ?? [])],
    'id',
  )

  const { field, fieldState } = useController({ control, name, rules })

  const selectedAssignee = field.value as WorkOrderAssigneeOption | null
  const errorMessage = fieldState.error?.message
  const isDisabled = disabled || !canLookUpAssignees

  const options = toAssigneeOptions(assignees, query ?? '')

  const clearQueryTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined)

  const handleChange = (option: WorkOrderAssigneeOption | null) => {
    clearTimeout(clearQueryTimeoutRef.current)
    field.onChange(option)
    field.onBlur()
    setQuery(null)
  }

  const handleBlur = () => {
    clearQueryTimeoutRef.current = setTimeout(() => {
      if (query?.trim() === '' && selectedAssignee) {
        field.onChange(null)
      }
      setQuery(null)
      field.onBlur()
    }, CLEAR_QUERY_DELAY_MS)
  }

  return (
    <FormField
      label={t('workOrdersComponents_assigneeField_label')}
      htmlFor={id}
      validation={
        errorMessage && (
          <FormFieldValidation type="error">{errorMessage}</FormFieldValidation>
        )
      }
    >
      <Tooltip disabled={canLookUpAssignees}>
        <TooltipTrigger>
          <div
            css={{
              position: 'relative',
              ...(isDisabled && { '& *': { cursor: 'not-allowed' } }),
            }}
          >
            <Combobox<WorkOrderAssigneeOption | null>
              id={id}
              name={field.name}
              size="compact"
              maxHeight="230px"
              disabled={isDisabled}
              error={Boolean(errorMessage)}
              options={options}
              value={selectedAssignee}
              query={query ?? selectedAssignee?.name ?? ''}
              onQueryChange={setQuery}
              onChange={handleChange}
              onBlur={handleBlur}
              optionToId={option => option?.id ?? ''}
              optionToQuery={option => option?.name ?? ''}
              renderOption={option => option?.name ?? ''}
              formatOption={option => option?.name ?? ''}
              emptyState={
                loading
                  ? t('workOrdersComponents_assigneeField_loading')
                  : t('workOrdersComponents_assigneeField_noAssigneeFound')
              }
            />
            {fieldState.isValidating && (
              <output
                aria-label={t('workOrdersComponents_assigneeField_validating')}
                css={theme => ({
                  position: 'absolute',
                  top: '50%',
                  // Clears the combobox chevron.
                  right: theme.space.Space8,
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  pointerEvents: 'none',
                })}
              >
                <LoadingIndicator size={16} />
              </output>
            )}
          </div>
        </TooltipTrigger>

        <TooltipContent css={{ zIndex: 1000 }}>
          {t('workOrdersComponents_assigneeField_noPermission_tooltip')}
        </TooltipContent>
      </Tooltip>
    </FormField>
  )
}
